import React from 'react'
import { To, useNavigate } from 'react-router-dom'

import { TIMEFRAME } from '../../constants'
import { Instrument } from '../../types'
import { useDialogState } from '../dialog'
import { getTimeframeFromDateRange } from './custom-date-range-utils'

interface IPathForTimeframeDateRangeProps {
  timeframe: TIMEFRAME | 'intraday'
  dateRange?: string | null
  ticker?: string
}

interface IUseCustomDateRangeDialogSubmitProps {
  anchorDate?: Date
  instrument?: Instrument
  shouldReloadOnNavigate?: boolean
  pathGenerator?: ({ ticker, timeframe, dateRange }: IPathForTimeframeDateRangeProps) => {
    pathname: string
    search: string
  }
}

export function useCustomDateRangeDialog({
  anchorDate,
  instrument,
  pathGenerator,
  shouldReloadOnNavigate = false,
}: IUseCustomDateRangeDialogSubmitProps) {
  const navigate = useNavigate()
  const customDateRangeDialogState = useDialogState()

  const isPremium = FinvizSettings.hasUserPremium

  const handleDateRangeSelect = React.useCallback(
    (
      { value, timeframe, href }: { value: string; timeframe?: TIMEFRAME; href?: To },
      callback?: ({ value, timeframe, href }: { value: string; timeframe: TIMEFRAME; href?: To }) => void
    ) => {
      if (!timeframe) return

      if (!href && pathGenerator) {
        const path = pathGenerator({ timeframe, dateRange: value })
        if (shouldReloadOnNavigate) {
          window.location.href = `${path.pathname}?${path.search}`
        } else {
          navigate(path)
        }
      }

      callback?.({ value, timeframe, href })
    },
    [navigate, pathGenerator, shouldReloadOnNavigate]
  )

  const getDateRangeTimeframe = React.useCallback(
    (dateRange: string | null) => {
      if (!anchorDate || !instrument || !dateRange) return null

      return {
        value: dateRange,
        timeframe: getTimeframeFromDateRange({
          dateRange: dateRange!,
          anchorDate,
          isPremium,
          instrument,
        }),
      }
    },
    [anchorDate, instrument, isPremium]
  )

  const onDialogSubmit = React.useCallback(
    ({ value: dateRange, timeframe: newTimeframe }: { value: string; timeframe: TIMEFRAME }) => {
      handleDateRangeSelect({
        value: dateRange,
        timeframe: newTimeframe,
      })
      customDateRangeDialogState.hide()
    },
    // We only want `customDateRangeDialogState.hide`, not the whole dialog
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customDateRangeDialogState.hide, handleDateRangeSelect]
  )

  return { onDialogSubmit, customDateRangeDialogState, handleDateRangeSelect, getDateRangeTimeframe }
}
